import type { Asset, StencilMask, TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';

export const getMediaIdFromAsset = (asset: Asset): string => {
    if (!VideoData.isMediaAsset(asset) || !asset.sourceData) {
        return '<unknown>';
    }

    const mediaRegistry = MediaRegistry.getInstance();
    const uuid = asset.sourceData.uuid;
    const mediaId = mediaRegistry.getItemIdByUuid(uuid);

    return mediaId || '<unknown>';
};

const getMediaViewport = (
    asset: TimeAwareMediaAsset,
): { x: number; y: number; width: number; height: number } => {
    if (asset.mask) {
        return (asset.mask as StencilMask).getMaxBounds();
    }
    const { width, height } = asset.root.referenceResolution;
    return { x: 0, y: 0, width, height };
};

export const getVisibleMediaBounds = (asset: TimeAwareMediaAsset): string => {
    const { x, y, width, height } = getMediaViewport(asset);
    return `${x} ${y} ${width} ${height}`;
};

export const getFullMediaResolution = (asset: TimeAwareMediaAsset): string => {
    return `${Math.round(asset.width)} ${Math.round(asset.height)}`;
};

/* Get the media translation in pixels. Since the media is always centered, these values are relative to the center of the visible area. */
export const getFullMediaTranslation = (asset: TimeAwareMediaAsset): string => {
    const { width, height } = getMediaViewport(asset);
    // get translation in pixels and remove sign
    let xTranslation = Math.abs(asset.xTranslation * width);
    let yTranslation = Math.abs(asset.yTranslation * height);

    // due to rounding, it's possible for our x or y translation to be out of range.
    // even though this will be absorbed by Luminary, it's confusing to the user so we want to correct it here.
    if (xTranslation > (asset.width - width) / 2.0) {
        xTranslation = (asset.width - width) / 2.0;
    }
    if (yTranslation > (asset.height - height) / 2.0) {
        yTranslation = (asset.height - height) / 2.0;
    }
    // add sign correction
    if (asset.xTranslation < 0) {
        xTranslation *= -1;
    }
    if (asset.yTranslation < 0) {
        yTranslation *= -1;
    }

    // the model always seems to be confused between left/right and up/down even though we provide it as part of the setMedia tool description
    // we're going to re-iterate it here for clarity
    const xDirection =
        asset.xTranslation === 0 ? 'center' : asset.xTranslation < 0 ? 'right' : 'left';
    const yDirection = asset.yTranslation === 0 ? 'center' : asset.yTranslation < 0 ? 'down' : 'up';

    return `${Math.round(xTranslation)}(${xDirection}) ${Math.round(yTranslation)}(${yDirection})`;
};

export const setFullMediaTranslation = (
    asset: TimeAwareMediaAsset,
    pixelsInX: number,
    pixelsInY: number,
) => {
    const { width, height } = getMediaViewport(asset);
    const xLimit = (asset.width - width) / 2.0;
    const yLimit = (asset.height - height) / 2.0;

    // Valid translation range is always [-abs(limit), +abs(limit)]
    if (Math.abs(pixelsInX) > Math.abs(xLimit)) {
        throw new Error(
            `Requested x translation ${pixelsInX} pixels exceeds limit of ±${Math.abs(xLimit).toFixed(2)} pixels`,
        );
    }
    if (Math.abs(pixelsInY) > Math.abs(yLimit)) {
        throw new Error(
            `Requested y translation ${pixelsInY} pixels exceeds limit of ±${Math.abs(yLimit).toFixed(2)} pixels`,
        );
    }
    asset.xTranslation = pixelsInX / asset.width;
    asset.yTranslation = pixelsInY / asset.height;
};

export const getFullMediaScale = (asset: TimeAwareMediaAsset): string => {
    const scale = Math.round(asset.scaleX * 100); // scaleX and scaleY should always be the same
    return `${scale}%`;
};

/* Get the full media translation range in pixels. This corresponds to how much the media can move within the visible area with the current scale value. We can increase the range by increasing the scale. */
export const getFullMediaTranslationRange = (
    asset: TimeAwareMediaAsset,
): { mediaXTranslationRange: string; mediaYTranslationRange: string } => {
    const { width, height } = getMediaViewport(asset);
    const xRange = Math.round((asset.width - width) / 2.0);
    const yRange = Math.round((asset.height - height) / 2.0);

    const mediaXTranslationRange = `-${xRange} ${xRange}`;
    const mediaYTranslationRange = `-${yRange} ${yRange}`;

    return { mediaXTranslationRange, mediaYTranslationRange };
};
